'use strict'
import api from '@/api/api'
import notify from '@/utils/notify'
import axios from 'axios'
import { decrypt } from '@/utils/crypto'

const buildQuery = (params = {}) => {
  const q = new URLSearchParams(params).toString()
  return q ? `?${q}` : ''
}

export default {
  // Reviews
  async getReviews(params = {}) {
    const data = { method: 'get', url: `reviews${buildQuery(params)}` }
    return await api.api(data)
  },

  async getManagerReviews(id) {
    const data = { method: 'get', url: `users/${id}/reviews` }
    return await api.api(data)
  },


  async createReview(json) {
    const data = { method: 'post', url: 'reviews', body: json }
    const res = await api.api(data)
    if (res.status === 201 || res.status === 200) notify.alert?.success?.('Performance review created')
    return res
  },

  async updateReview(id, json) {
    const data = { method: 'put', url: `reviews/${id}`, body: json }
    const res = await api.api(data)
    if (res.status === 200) notify.alert?.success?.('Performance review updated')
    return res
  },

  async getReview(id) {
    const data = { method: 'get', url: `reviews/${id}` }
    return await api.api(data)
  },

  // Self Review
  async submitSelfReview(id, json) {
    const data = { method: 'post', url: `reviews/${id}/self`, body: json }
    const res = await api.api(data)
    if (res.status === 200) notify.alert?.success?.('Self review submitted')
    return res
  },

  // Update scores for self review responses
  async updateScores(id, json) {
    const data = { method: 'put', url: `reviews/${id}/updateScores`, body: json }
    const res = await api.api(data)
    if (res.status === 200) notify.alert?.success?.('Scores updated')
    return res
  },

  // Manager Review
  async submitManagerReview(id, json) {
    const data = { method: 'post', url: `reviews/${id}/manager`, body: json }
    const res = await api.api(data)
    if (res.status === 200) notify.alert?.success?.('Manager review submitted')
    return res
  },

  // Submit Review (generic)
  async submitReview(id, json) {
    const data = { method: 'post', url: `reviews/${id}/submit`, body: json }
    const res = await api.api(data)
    if (res.status === 200) notify.alert?.success?.('Review submitted')
    return res
  },

  // Acknowledge Review
  async acknowledgeReview(id) {
    const data = { method: 'post', url: `reviews/${id}/acknowledge` }
    const res = await api.api(data)
    if (res.status === 200) notify.alert?.success?.('Review acknowledged')
    return res
  },

  // 360 Feedback
  async request360Feedback(id, users) {
    const data = { method: 'post', url: `reviews/${id}/feedback`, body: { users } }
    const res = await api.api(data)
    if (res.status === 200) notify.alert?.success?.('360 feedback requests sent')
    return res
  },

  // Comments (on a review)
  async addComment(id, json) {
    const data = { method: 'post', url: `reviews/${id}/comments`, body: json }
    const res = await api.api(data)
    if (res.status === 200) notify.alert?.success?.('Comment added')
    return res
  },

  async getComments(id) {
    const data = { method: 'get', url: `reviews/${id}/comments` }
    return await api.api(data)
  },

  async replyToComment(reviewId, commentId, json) {
    const data = { method: 'post', url: `reviews/${reviewId}/comments/${commentId}/reply`, body: json }
    const res = await api.api(data)
    if (res.status === 200) notify.alert?.success?.('Reply posted')
    return res
  },

  // Report
  async getReport(id) {
    const data = { method: 'get', url: `reviews/${id}/report` }
    return await api.api(data)
  },

  // Analytics
  async getAnalytics(params = {}) {
    const data = { method: 'get', url: `performance/analytics${buildQuery(params)}` }
    return await api.api(data)
  },

  async getCycleAnalytics(id) {
    const data = { method: 'get', url: `review-cycles/${id}/analytics` }
    return await api.api(data)
  },

  async generateAnalyticsReport(json) {
    const data = { method: 'post', url: 'performance/analytics/report', body: json }
    return await api.api(data)
  },

  // Export (use axios directly to ensure blob response)
  async exportAnalytics(params = {}) {
    const token = decrypt(sessionStorage.getItem('access_token'))
    const AUTH_TOKEN = `Bearer ${token}`
    const url = `${process.env.VUE_APP_API_URL}performance/analytics/export${buildQuery(params)}`
    const res = await axios.get(url, {
      responseType: 'blob',
      headers: {
        Authorization: AUTH_TOKEN,
        Accept: 'application/octet-stream'
      }
    })
    return { status: res.status, data: res.data }
  },

  // Review Cycles
  async getReviewCycles(params = {}) {
    const data = { method: 'get', url: `review-cycles${buildQuery(params)}` }
    return await api.api(data)
  },

  async getEmployeeReviews(id) {
    const data = { method: 'get', url: `employees/${id}/reviews` }
    return await api.api(data)
  },

  async getActiveReviewCycle() {
    const data = { method: 'get', url: 'review-cycles/active' }
    return await api.api(data)
  },

  // Templates
  async getReviewTemplates() {
    const data = { method: 'get', url: 'performance/review-templates' }
    return await api.api(data)
  },

  async getReviewTemplate(id) {
    const data = { method: 'get', url: `performance/review-templates/${id}` }
    return await api.api(data)
  },

  async getTemplate(id) {
    const data = { method: 'get', url: `performance/review-templates/${id}` }
    return await api.api(data)
  },

  async createTemplate(json) {
    const data = { method: 'post', url: 'performance/review-templates', body: json }
    const res = await api.api(data)
    if (res.status === 201 || res.status === 200) notify.alert?.success?.('Template created')
    return res
  },

  async updateTemplate(id, json) {
    const data = { method: 'put', url: `performance/review-templates/${id}`, body: json }
    const res = await api.api(data)
    if (res.status === 200) notify.alert?.success?.('Template updated')
    return res
  },

  async deleteTemplate(id) {
    const data = { method: 'delete', url: `performance/review-templates/${id}` }
    const res = await api.api(data)
    if (res.status === 200) notify.alert?.success?.('Template deleted')
    return res
  },

  // Review Criteria
  async getTemplateCriteria(templateId) {
    const data = { method: 'get', url: `performance/review-templates/${templateId}/criteria` }
    return await api.api(data)
  },

  async createCriteria(json) {
    const data = { method: 'post', url: 'performance/review-criteria', body: json }
    const res = await api.api(data)
    if (res.status === 201 || res.status === 200) notify.alert?.success?.('Criteria created')
    return res
  },

  async updateCriteria(id, json) {
    const data = { method: 'put', url: `performance/review-criteria/${id}`, body: json }
    const res = await api.api(data)
    if (res.status === 200) notify.alert?.success?.('Criteria updated')
    return res
  },

  async deleteCriteria(id) {
    const data = { method: 'delete', url: `performance/review-criteria/${id}` }
    const res = await api.api(data)
    if (res.status === 200) notify.alert?.success?.('Criteria deleted')
    return res
  },

  // Goals
  async getGoals(reviewId) {
    const data = { method: 'get', url: `reviews/${reviewId}/goals` }
    return await api.api(data)
  },

  async updateGoal(reviewId, goalId, json) {
    const data = { method: 'put', url: `reviews/${reviewId}/goals/${goalId}`, body: json }
    const res = await api.api(data)
    if (res.status === 200) notify.alert?.success?.('Goal updated')
    return res
  },

  async createGoal(reviewId, json) {
    const data = { method: 'post', url: `reviews/${reviewId}/goals`, body: json }
    const res = await api.api(data)
    if (res.status === 201 || res.status === 200) notify.alert?.success?.('Goal created')
    return res
  },

  async deleteGoal(reviewId, goalId) {
    const data = { method: 'delete', url: `reviews/${reviewId}/goals/${goalId}` }
    const res = await api.api(data)
    if (res.status === 200) notify.alert?.success?.('Goal deleted')
    return res
  },

  // Feedback requests (review scoped)
  async getFeedbackRequests(reviewId) {
    const data = { method: 'get', url: `/feedback-requests/performance/${reviewId}` }
    return await api.api(data)
  },

  async getFeedbackRequest(requestId) {
    const data = { method: 'get', url: `feedback-requests/${requestId}` }
    return await api.api(data)
  },

  async submitFeedback(json) {
    const data = { method: 'post', url: 'performance/feedback/submit', body: json }
    return await api.api(data)
  },

  async sendFeedbackReminder(requestId) {
    const data = { method: 'post', url: `performance/feedback-requests/${requestId}/remind` }
    return await api.api(data)
  },

  async declineFeedback(token) {
    const data = { method: 'post', url: `performance/feedback/${token}/decline` }
    return await api.api(data)
  },


}
